import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { shipmentsAPI, uploadAPI } from '../services/api';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import AddressDropdown from '../components/AddressDropdown';
import HTSCodeModal from '../components/HTSCodeModal';
import RejectionBanner from '../components/RejectionBanner';
import './NewShipment.css';

function NewIntlShipment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [htsModalOpen, setHtsModalOpen] = useState(false);
  const [selectedHTS, setSelectedHTS] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editShipmentId, setEditShipmentId] = useState(null);
  const [editRejectionData, setEditRejectionData] = useState(null);

  const [formData, setFormData] = useState({
    shipment_type: 'international',
    shipper_name: '',
    shipper_address: '',
    shipper_address2: '',
    shipper_city: '',
    shipper_state: '',
    shipper_zip: '',
    shipper_country: 'US',
    shipper_contact: '',
    shipper_phone: '',
    shipper_email: '',
    shipper_charge_code: '',
    shipper_reference_num: '',
    shipper_adp_num: '',
    shipper_ncr_num: '',
    purchase_order: '',
    production_order: '',
    consignee_name: '',
    consignee_address: '',
    consignee_address2: '',
    consignee_city: '',
    consignee_state: '',
    consignee_zip: '',
    consignee_country: '',
    consignee_contact: '',
    consignee_phone: '',
    consignee_email: '',
    consignee_reference_num: '',
    pickup_date: '',
    pickup_window_start: '08:59',
    pickup_window_end: '17:59',
    delivery_date: '',
    delivery_window_start: '08:59',
    delivery_window_end: '17:59',
    brokerage_name: '',
    brokerage_number: '',
    incoterm: '',
    incoterm_location: '',
    tib_required: '',
    active_outage: '',
    original_value_per_piece: '',
    added_value_per_piece: '',
    import_export: '',
    finished_turbine_part: '',
    hts_code_selection: '',
    hts_description: '',
    country_of_origin: '',
    product_location: '',
    special_instructions: '',
  });

  const [lineItems, setLineItems] = useState([]);

  // Handle cloned shipment data
  useEffect(() => {
    if (location.state?.cloneData) {
      const cloneData = location.state.cloneData;
      // Normalize boolean fields — MySQL returns 0/1 integers, React needs true/false
      const booleanFields = [
        'ots', 'finished_turbine_part', 'acc_liftgate_air', 'acc_liftgate_gr',
        'acc_inside_air', 'acc_inside_gr', 'acc_rigging_air', 'acc_rigging_gr',
        'acc_construction_air', 'acc_construction_gr', 'acc_tarp_needed', 'acc_protect_freezing'
      ];
      const normalizedCloneData = { ...cloneData };
      booleanFields.forEach(field => {
        if (field in normalizedCloneData) {
          normalizedCloneData[field] = !!normalizedCloneData[field];
        }
      });
      setFormData((prev) => ({
        ...prev,
        ...normalizedCloneData,
        // Clear ID and dates for new shipment
        id: undefined,
        request_date: undefined,
        created_at: undefined,
        updated_at: undefined,
      }));

      // Set line items if available
      if (cloneData.line_items && cloneData.line_items.length > 0) {
        setLineItems(
          cloneData.line_items.map((item) => ({
            ...item,
            id: undefined,
            shipment_id: undefined,
            locked: false,
          }))
        );
      }

      // Set HTS code if available
      if (cloneData.hts_code_selection) {
        setSelectedHTS({ code: cloneData.hts_code_selection });
      }

      // Show message that data was cloned
      setMessage({
        type: 'success',
        text: 'Shipment data cloned successfully. Review and submit to create a new shipment.',
      });

      // Scroll to top to show message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state]);

  // Handle edit mode (resubmit rejected shipment)
  useEffect(() => {
    if (location.state?.editMode && location.state?.editData) {
      const editData = location.state.editData;
      setEditMode(true);
      setEditShipmentId(editData.id);
      setEditRejectionData({
        rejection_reason: editData.rejection_reason,
        rejected_by_name: editData.rejected_by_name,
        rejected_at: editData.rejected_at,
      });

      setFormData((prev) => ({
        ...prev,
        ...editData,
      }));

      // Pre-populate line items if available
      if (editData.line_items && editData.line_items.length > 0) {
        setLineItems(editData.line_items);
      }

      // Set HTS code if available
      if (editData.hts_code_selection) {
        setSelectedHTS({ code: editData.hts_code_selection });
      }

      setMessage({
        type: 'info',
        text: 'Edit your shipment and submit to resubmit for approval.',
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (location.state?.editMode && !location.state?.editData) {
      navigate('/dashboard');
    }
  }, [location.state, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleShipperSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      shipper_name: address.name || '',
      shipper_address: address.address || '',
      shipper_address2: address.address2 || '',
      shipper_city: address.city || '',
      shipper_state: address.state || '',
      shipper_zip: address.zip || '',
      shipper_country: address.country || 'US',
      shipper_contact: address.contact || '',
      shipper_phone: address.phone || '',
      shipper_email: address.email || '',
    }));
  };

  const handleConsigneeSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      consignee_name: address.name || '',
      consignee_address: address.address || '',
      consignee_address2: address.address2 || '',
      consignee_city: address.city || '',
      consignee_state: address.state || '',
      consignee_zip: address.zip || '',
      consignee_country: address.country || '',
      consignee_contact: address.contact || '',
      consignee_phone: address.phone || '',
      consignee_email: address.email || '',
    }));
  };

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        pieces: '',
        type: '',
        weight_kgs: '',
        net_weight_kgs: '',
        length_cm: '',
        width_cm: '',
        height_cm: '',
        description: '',
        class: '',
        locked: false,
      },
    ]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const lockLineItem = (index) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], locked: true };
      return updated;
    });
  };

  const unlockLineItem = (index) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], locked: false };
      return updated;
    });
  };

  const removeLineItem = (index) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleHTSSelect = (hts) => {
    setSelectedHTS(hts);
    setFormData((prev) => ({ ...prev, hts_code_selection: hts.code }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Combine with existing files
    const combined = [...selectedFiles, ...newFiles];
    
    // Validate max 10 files total
    if (combined.length > 10) {
      setMessage({
        type: 'error',
        text: `Maximum 10 files allowed. You already have ${selectedFiles.length} selected.`,
      });
      e.target.value = '';
      return;
    }
    
    // Validate each new file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const invalidFiles = newFiles.filter(file => file.size > maxSize);
    if (invalidFiles.length > 0) {
      setMessage({
        type: 'error',
        text: `File(s) too large: ${invalidFiles.map(f => f.name).join(', ')}. Max size is 10MB per file.`,
      });
      e.target.value = '';
      return;
    }
    
    setSelectedFiles(combined);
    e.target.value = ''; // Reset input so same file can be added again
  };

  const handleClearForm = () => {
    if (window.confirm('Are you sure you want to clear the form?')) {
      window.location.reload();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Comprehensive validation
    const errors = [];
    
    // Shipper - Required fields
    if (!formData.shipper_name?.trim()) errors.push('Shipper Name is required');
    if (!formData.shipper_address?.trim()) errors.push('Shipper Address is required');
    if (!formData.shipper_city?.trim()) errors.push('Shipper City is required');
    if (!formData.shipper_state?.trim()) errors.push('Shipper State is required');
    if (!formData.shipper_zip?.trim()) errors.push('Shipper ZIP is required');
    if (!formData.shipper_country?.trim()) errors.push('Shipper Country is required');
    if (!formData.shipper_contact?.trim()) errors.push('Shipper Contact is required');
    if (!formData.shipper_phone?.trim()) errors.push('Shipper Phone is required');
    if (!formData.shipper_email?.trim()) errors.push('Shipper Email is required');
    if (!formData.shipper_reference_num?.trim()) errors.push('Shipper Reference Number is required');
    if (!formData.shipper_charge_code?.trim()) errors.push('Shipper Charge Code is required');
    
    // Purchase Order and Production Order
    if (!formData.purchase_order?.trim()) errors.push('Purchase Order is required (enter PO # or N/A)');
    if (!formData.production_order?.trim()) errors.push('Production Order is required (enter Prod # or NA)');

    // Consignee - Required fields
    if (!formData.consignee_name?.trim()) errors.push('Consignee Name is required');
    if (!formData.consignee_address?.trim()) errors.push('Consignee Address is required');
    if (!formData.consignee_city?.trim()) errors.push('Consignee City is required');
    if (!formData.consignee_state?.trim()) errors.push('Consignee State is required');
    if (!formData.consignee_zip?.trim()) errors.push('Consignee ZIP is required');
    if (!formData.consignee_country?.trim()) errors.push('Consignee Country is required');
    if (!formData.consignee_contact?.trim()) errors.push('Consignee Contact is required');
    if (!formData.consignee_phone?.trim()) errors.push('Consignee Phone is required');
    if (!formData.consignee_email?.trim()) errors.push('Consignee Email is required');
    if (!formData.consignee_reference_num?.trim()) errors.push('Consignee Reference Number is required');

    // Pickup/Delivery
    if (!formData.pickup_date) errors.push('Pickup Date is required');
    if (!formData.delivery_date) errors.push('Delivery Date is required');
    if (!formData.pickup_window_start) errors.push('Pickup Window Start is required');
    if (!formData.pickup_window_end) errors.push('Pickup Window End is required');
    if (!formData.delivery_window_start) errors.push('Delivery Window Start is required');
    if (!formData.delivery_window_end) errors.push('Delivery Window End is required');

    // TIB and Active Outage
    if (!formData.tib_required) errors.push('TIB (Temporary Import Bond) selection is required');
    if (!formData.active_outage) errors.push('Active Outage selection is required');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.shipper_email && !emailRegex.test(formData.shipper_email)) {
      errors.push('Shipper Email format is invalid');
    }
    if (formData.consignee_email && !emailRegex.test(formData.consignee_email)) {
      errors.push('Consignee Email format is invalid');
    }

    // Validate phone numbers (international format)
    const phoneRegex = /^[\d\s\-\(\)\.+]{7,}$/; // Allow international formats with + and at least 7 digits
    
    if (formData.shipper_phone && !phoneRegex.test(formData.shipper_phone)) {
      errors.push('Shipper Phone format is invalid. Please use international format (e.g., +1-234-567-8900 or +44-20-1234-5678)');
    }
    if (formData.consignee_phone && !phoneRegex.test(formData.consignee_phone)) {
      errors.push('Consignee Phone format is invalid. Please use international format (e.g., +1-234-567-8900 or +44-20-1234-5678)');
    }

    if (errors.length > 0) {
      setMessage({ type: 'error', text: errors.join('. ') });
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const submitData = {
        ...formData,
        lineItems: lineItems.filter((item) => 
          // Keep line item if ANY field has a value
          item.pieces || item.type || 
          item.weight_kgs || item.net_weight_kgs ||
          item.length_cm || item.width_cm || item.height_cm || 
          item.description || item.class
        ),
      };

      // Edit mode: call update (resubmit)
      if (editMode && editShipmentId) {
        try {
          await shipmentsAPI.update(editShipmentId, submitData);
          setMessage({
            type: 'success',
            text: 'Shipment resubmitted successfully! Redirecting to My Shipments...',
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } catch (err) {
          if (err.response?.status === 409) {
            setMessage({
              type: 'error',
              text: 'This shipment can no longer be edited in its current state.',
            });
          } else if (err.response?.status === 500) {
            setMessage({
              type: 'error',
              text: 'Resubmit failed — admin notification could not be sent. Please try again.',
            });
          } else {
            setMessage({
              type: 'error',
              text: err.response?.data?.message || 'Failed to resubmit shipment',
            });
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setLoading(false);
        return;
      }

      // Create mode: original flow
      const response = await shipmentsAPI.create(submitData);
      const shipmentId = response.data.shipment?.id || response.data.shipmentId;

      if (selectedFiles.length > 0 && shipmentId) {
        try {
          // Upload all files
          const uploadPromises = selectedFiles.map(file => 
            uploadAPI.uploadFile(file, shipmentId)
          );
          await Promise.all(uploadPromises);
          
          setMessage({
            type: 'success',
            text: `International shipment and ${selectedFiles.length} file(s) uploaded successfully! Sending notification...`,
          });
        } catch (uploadErr) {
          console.error('File upload error:', uploadErr);
          setMessage({
            type: 'success',
            text: 'International shipment created but some files failed to upload. Sending notification...',
          });
        }
      } else {
        setMessage({
          type: 'success',
          text: 'International shipment created successfully! Sending notification...',
        });
      }

      // Send email notification after everything is persisted
      try {
        console.log(`[Shipment ${shipmentId}] Sending email notification...`);
        await shipmentsAPI.sendNotification(shipmentId);
        console.log(`[Shipment ${shipmentId}] Email notification sent successfully`);
        setMessage({
          type: 'success',
          text: 'International shipment created and notification sent! Redirecting to My Shipments...',
        });
      } catch (notifErr) {
        // Don't fail if notification fails, but log the error
        console.error(`[Shipment ${shipmentId}] Failed to send notification:`, notifErr);
        setMessage({
          type: 'warning',
          text: 'International shipment created but email notification failed. Please contact support. Redirecting to My Shipments...',
        });
      }

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Redirect after 3 seconds to give user time to see the message
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (err) {
      console.error('Shipment creation error:', err);
      console.error('Error response:', err.response);

      let errorMessage = 'Failed to create shipment';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Add more details if available
      if (err.response?.status) {
        errorMessage += ` (Status: ${err.response.status})`;
      }

      setMessage({ type: 'error', text: errorMessage });

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="new-intl-shipment" />

      <div className="content">
        <h1 className="page-title">{editMode ? 'Edit & Resubmit International Shipment' : 'International Shipment Entry'}</h1>

        {editMode && editRejectionData && (
          <RejectionBanner
            rejectionReason={editRejectionData.rejection_reason}
            rejectedByName={editRejectionData.rejected_by_name}
            rejectedAt={editRejectionData.rejected_at}
          />
        )}

        <div className="note-box">
          <strong>Note:</strong> All international shipments will be booked within 2-3 business
          days. Questions on Shipping Portal Request should be directed to the Logistics Team -{' '}
          <a href="mailto:psmshipment@us.psm.com" className="contact-link">
            psmshipment@us.psm.com
          </a>
          .
        </div>

        {message.text && (
          <div className={message.type === 'error' ? 'error-message' : 'success-message'}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-column">
              <h3 className="section-title">Shipper</h3>
              <AddressDropdown type="shipper" onSelect={handleShipperSelect} />
              <div className="form-field">
                <label>
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_name"
                  value={formData.shipper_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_address"
                  value={formData.shipper_address}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="shipper_address2"
                  value={formData.shipper_address2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_city"
                  value={formData.shipper_city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_state"
                  value={formData.shipper_state}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Zip <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="shipper_zip"
                  value={formData.shipper_zip}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Country <span className="required">*</span>
                </label>
                <select
                  name="shipper_country"
                  value={formData.shipper_country}
                  onChange={handleInputChange}
                  required
                >
                  <option value="US">US - United States</option>
                  <option value="CA">CA - Canada</option>
                  <option value="MX">MX - Mexico</option>
                  <option value="DE">DE - Germany</option>
                  <option value="GB">GB - United Kingdom</option>
                  <option value="FR">FR - France</option>
                  <option value="JP">JP - Japan</option>
                  <option value="CN">CN - China</option>
                  <option value="KR">KR - South Korea</option>
                  <option value="AU">AU - Australia</option>
                  <option value="IN">IN - India</option>
                  <option value="BR">BR - Brazil</option>
                  <option value="CH">CH - Switzerland</option>
                  <option value="IT">IT - Italy</option>
                  <option value="ES">ES - Spain</option>
                  <option value="NL">NL - Netherlands</option>
                  <option value="SE">SE - Sweden</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label>Contact <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_contact"
                  value={formData.shipper_contact}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Phone Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_phone"
                  value={formData.shipper_phone}
                  onChange={handleInputChange}
                  placeholder="e.g., +1-234-567-8900"
                />
              </div>
              <div className="form-field">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="shipper_email"
                  value={formData.shipper_email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Charge Code <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_charge_code"
                  value={formData.shipper_charge_code}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Reference Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="shipper_reference_num"
                  value={formData.shipper_reference_num}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>ADP Num</label>
                <input
                  type="text"
                  name="shipper_adp_num"
                  value={formData.shipper_adp_num}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>NCR Num</label>
                <input
                  type="text"
                  name="shipper_ncr_num"
                  value={formData.shipper_ncr_num}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Purchase Order <span className="required">*</span></label>
                <input
                  type="text"
                  name="purchase_order"
                  value={formData.purchase_order}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Production Order <span className="required">*</span></label>
                <input
                  type="text"
                  name="production_order"
                  value={formData.production_order}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-column">
              <h3 className="section-title">Consignee</h3>
              <AddressDropdown type="consignee" onSelect={handleConsigneeSelect} />
              <div className="form-field">
                <label>
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_name"
                  value={formData.consignee_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_address"
                  value={formData.consignee_address}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Address 2</label>
                <input
                  type="text"
                  name="consignee_address2"
                  value={formData.consignee_address2}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_city"
                  value={formData.consignee_city}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_state"
                  value={formData.consignee_state}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Zip <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="consignee_zip"
                  value={formData.consignee_zip}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>
                  Country <span className="required">*</span>
                </label>
                <select
                  name="consignee_country"
                  value={formData.consignee_country}
                  onChange={handleInputChange}
                  required
                >
                  <option value="US">US - United States</option>
                  <option value="CA">CA - Canada</option>
                  <option value="MX">MX - Mexico</option>
                  <option value="DE">DE - Germany</option>
                  <option value="GB">GB - United Kingdom</option>
                  <option value="FR">FR - France</option>
                  <option value="JP">JP - Japan</option>
                  <option value="CN">CN - China</option>
                  <option value="KR">KR - South Korea</option>
                  <option value="AU">AU - Australia</option>
                  <option value="IN">IN - India</option>
                  <option value="BR">BR - Brazil</option>
                  <option value="CH">CH - Switzerland</option>
                  <option value="IT">IT - Italy</option>
                  <option value="ES">ES - Spain</option>
                  <option value="NL">NL - Netherlands</option>
                  <option value="SE">SE - Sweden</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label>Contact <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_contact"
                  value={formData.consignee_contact}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Phone Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_phone"
                  value={formData.consignee_phone}
                  onChange={handleInputChange}
                  placeholder="e.g., +44-20-1234-5678"
                />
              </div>
              <div className="form-field">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="consignee_email"
                  value={formData.consignee_email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Reference Num <span className="required">*</span></label>
                <input
                  type="text"
                  name="consignee_reference_num"
                  value={formData.consignee_reference_num}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-column">
              <h3 className="section-title">Pickup</h3>
              <p className="note-text" style={{ fontSize: '0.85rem', color: '#856404', background: '#fff3cd', padding: '8px 12px', borderRadius: '4px', margin: '0 0 15px 0' }}>
                <strong>Note:</strong> All shipments entered after 2:00 PM EST will be processed the following business day.
              </p>
              <div className="form-field">
                <label>Pickup Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>PU Window Start <span class="required">*</span></label>
                <input
                  type="time"
                  name="pickup_window_start"
                  value={formData.pickup_window_start}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>PU Window End <span class="required">*</span></label>
                <input
                  type="time"
                  name="pickup_window_end"
                  value={formData.pickup_window_end}
                  onChange={handleInputChange}
                />
              </div>

              <h3 className="section-title" style={{ marginTop: '30px' }}>
                Customs Brokerage
              </h3>
              <div className="form-field">
                <label>Brokerage Name</label>
                <input
                  type="text"
                  name="brokerage_name"
                  value={formData.brokerage_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Brokerage Number</label>
                <input
                  type="text"
                  name="brokerage_number"
                  value={formData.brokerage_number}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field">
                <label>TIB (Temporary Import Bond)? <span className="required">*</span></label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input type="radio" name="tib_required" value="true" checked={formData.tib_required === 'true'} onChange={handleInputChange} />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input type="radio" name="tib_required" value="false" checked={formData.tib_required === 'false'} onChange={handleInputChange} />
                    No
                  </label>
                </div>
              </div>

              <div className="form-field">
                <label>Is this an Active Outage? <span className="required">*</span></label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input type="radio" name="active_outage" value="true" checked={formData.active_outage === 'true'} onChange={handleInputChange} />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input type="radio" name="active_outage" value="false" checked={formData.active_outage === 'false'} onChange={handleInputChange} />
                    No
                  </label>
                </div>
              </div>
              <div className="form-field">
                <label>Incoterm</label>
                <select name="incoterm" value={formData.incoterm} onChange={handleInputChange}>
                  <option value="">Select...</option>
                  <option value="EXW">EX WORKS (EXW)</option>
                  <option value="FCA">FREE CARRIER (FCA)</option>
                  <option value="CPT">CARRIAGE PAID TO (CPT)</option>
                  <option value="CIF">CARRIAGE & INSURANCE PAID TO (CIF)</option>
                  <option value="DAT">DELIVERED AT TERMINAL (DAT)</option>
                  <option value="DAP">DELIVERED AT PLACE (DAP)</option>
                  <option value="DDP">DELIVERED DUTY PAID (DDP)</option>
                  <option value="FAS">FREE ALONGSIDE SHIP (FAS)</option>
                  <option value="FOB">FREE ON BOARD (FOB)</option>
                </select>
              </div>
              <div className="form-field">
                <label>Incoterm Location</label>
                <input
                  type="text"
                  name="incoterm_location"
                  value={formData.incoterm_location}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-column">
              <h3 className="section-title">Delivery</h3>
              <div className="form-field">
                <label>Delivery Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="delivery_date"
                  value={formData.delivery_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>DEL Window Start <span class="required">*</span></label>
                <input
                  type="time"
                  name="delivery_window_start"
                  value={formData.delivery_window_start}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>DEL Window End <span class="required">*</span></label>
                <input
                  type="time"
                  name="delivery_window_end"
                  value={formData.delivery_window_end}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-field">
                <label>Original Value Per Piece</label>
                <input
                  type="number"
                  step="0.01"
                  name="original_value_per_piece"
                  value={formData.original_value_per_piece}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Added Value Per Piece</label>
                <input
                  type="number"
                  step="0.01"
                  name="added_value_per_piece"
                  value={formData.added_value_per_piece}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Import/Export</label>
                <select
                  name="import_export"
                  value={formData.import_export}
                  onChange={handleInputChange}
                >
                  <option value="">Select...</option>
                  <option value="temporary">Temporary</option>
                  <option value="permanent">Permanent</option>
                </select>
              </div>
              <div className="form-field">
                <label>Finished Turbine Part</label>
                <select
                  name="finished_turbine_part"
                  value={formData.finished_turbine_part}
                  onChange={handleInputChange}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="form-field">
                <label>HTS Code Selection</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={selectedHTS ? `${selectedHTS.code} - ${selectedHTS.description}` : ''}
                    placeholder="Click 'Browse' to select HTS code..."
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setHtsModalOpen(true)}
                    className="browse-button"
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                  >
                    Browse...
                  </button>
                </div>
              </div>
              <div className="form-field">
                <label>Or Enter HTS Code Manually</label>
                <input
                  type="text"
                  name="hts_code_selection"
                  value={formData.hts_code_selection}
                  onChange={(e) => {
                    handleInputChange(e);
                    // Clear selected HTS when manually entering
                    if (e.target.value) {
                      setSelectedHTS(null);
                    }
                  }}
                  placeholder="Enter HTS code number (e.g., 8411.11.0000)"
                />
              </div>
              <div className="form-field">
                <label>HTS Code Description (Optional)</label>
                <input
                  type="text"
                  name="hts_description"
                  value={formData.hts_description}
                  onChange={handleInputChange}
                  placeholder="Enter description for the HTS code"
                />
                <div
                  className="field-note"
                  style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}
                >
                  If the HTS code is not in our database, enter both code and description. This will
                  be saved for future use.
                </div>
              </div>
              <div className="form-field">
                <label>Country of Origin</label>
                <input
                  type="text"
                  name="country_of_origin"
                  value={formData.country_of_origin}
                  onChange={handleInputChange}
                />
              </div>
              <div
                className="field-note"
                style={{ color: '#ff4444', fontWeight: '500', marginTop: '10px' }}
              >
                *If additional space is needed, please use special instructions section below.
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Line Items</h3>
            <div className="line-items-container">
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Pieces</th>
                    <th>Type</th>
                    <th>Weight (kgs)</th>
                    <th>Net Weight (kgs)</th>
                    <th>Length (cm)</th>
                    <th>Width (cm)</th>
                    <th>Height (cm)</th>
                    <th>Description</th>
                    <th>Class</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">
                        New
                        <br />
                        No data to display
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((item, index) => (
                      <tr key={index} className={item.locked ? 'locked-row' : ''}>
                        <td>{index + 1}</td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.pieces}</span>
                          ) : (
                            <input
                              type="text"
                              value={item.pieces}
                              onChange={(e) => updateLineItem(index, 'pieces', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.type}</span>
                          ) : (
                            <select
                              value={item.type}
                              onChange={(e) => updateLineItem(index, 'type', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="Pallets">Pallets</option>
                              <option value="Pieces">Pieces</option>
                              <option value="Crates">Crates</option>
                              <option value="Cases">Cases</option>
                              <option value="Boxes">Boxes</option>
                            </select>
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.weight_kgs}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.weight_kgs}
                              onChange={(e) => updateLineItem(index, 'weight_kgs', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.net_weight_kgs}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.net_weight_kgs}
                              onChange={(e) =>
                                updateLineItem(index, 'net_weight_kgs', e.target.value)
                              }
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.length_cm}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.length_cm}
                              onChange={(e) => updateLineItem(index, 'length_cm', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.width_cm}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.width_cm}
                              onChange={(e) => updateLineItem(index, 'width_cm', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.height_cm}</span>
                          ) : (
                            <input
                              type="number"
                              value={item.height_cm}
                              onChange={(e) => updateLineItem(index, 'height_cm', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.description}</span>
                          ) : (
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <span className="locked-value">{item.class}</span>
                          ) : (
                            <select
                              value={item.class}
                              onChange={(e) => updateLineItem(index, 'class', e.target.value)}
                            >
                              <option value="">Select...</option>
                              <option value="50">Class 50</option>
                              <option value="55">Class 55</option>
                              <option value="60">Class 60</option>
                              <option value="65">Class 65</option>
                              <option value="70">Class 70</option>
                              <option value="77.5">Class 77.5</option>
                              <option value="85">Class 85</option>
                              <option value="92.5">Class 92.5</option>
                              <option value="100">Class 100</option>
                              <option value="110">Class 110</option>
                              <option value="125">Class 125</option>
                              <option value="150">Class 150</option>
                              <option value="175">Class 175</option>
                              <option value="200">Class 200</option>
                              <option value="250">Class 250</option>
                              <option value="300">Class 300</option>
                              <option value="400">Class 400</option>
                              <option value="500">Class 500</option>
                            </select>
                          )}
                        </td>
                        <td>
                          {item.locked ? (
                            <>
                              <button
                                type="button"
                                className="edit-button"
                                onClick={() => unlockLineItem(index)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="remove-button"
                                onClick={() => removeLineItem(index)}
                              >
                                Remove
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="update-button"
                                onClick={() => lockLineItem(index)}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="cancel-button"
                                onClick={() => removeLineItem(index)}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <button type="button" className="add-button" onClick={addLineItem}>
                New
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Product Location</h3>
            <div className="form-field">
              <select
                name="product_location"
                value={formData.product_location}
                onChange={handleInputChange}
              >
                <option value="">Select...</option>
                <option value="New Make">New Make</option>
                <option value="Ordered from EWMS">Ordered from EWMS</option>
                <option value="RECO Packing">RECO Packing</option>
                <option value="Requestors location (cubicle/office)">
                  Requestors location (cubicle/office)
                </option>
                <option value="Shipping Department">Shipping Department</option>
                <option value="Vendor Location">Vendor Location</option>
                <option value="Ventex">Ventex</option>
                <option value="Will Deliver to Shipping">Will Deliver to Shipping</option>
                <option value="WPB Warehouse">WPB Warehouse</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">File Attachments (Max 10)</h3>
            <div className="form-field">
              <input
                type="file"
                name="file_attachment"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                multiple
              />
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ color: '#4CAF50', fontSize: '13px', marginBottom: '5px' }}>
                    {selectedFiles.length} file(s) selected:
                  </div>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                      • {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </div>
                  ))}
                </div>
              )}
              <div className="field-note">
                Browse and select up to 10 files from your local computer (PDF, DOC, DOCX, XLS, XLSX, JPG,
                PNG - Max 10MB per file)
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Special Instructions</h3>
            <div className="form-field">
              <textarea
                name="special_instructions"
                value={formData.special_instructions}
                onChange={handleInputChange}
                rows="5"
              />
            </div>
            <div className="field-note">
              If additional space is needed, please use special instructions section below
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="clear-button"
              onClick={handleClearForm}
              disabled={loading}
            >
              Clear Form
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>

      <HTSCodeModal
        isOpen={htsModalOpen}
        onClose={() => setHtsModalOpen(false)}
        onSelect={handleHTSSelect}
        selectedCode={formData.hts_code_selection}
      />
    </div>
  );
}

export default NewIntlShipment;

